import { Loading } from "../../app/Status";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { formatEuro, formatQty, type ObligationMaterialsResponse } from "@klantpunt/shared";
import { IconPlus, IconTrash } from "../../app/icons";
import { api } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { parseQty } from "./MaterialDialogs";
import { errMsg, materialsKey, useMaterials, useObligationMaterials } from "./useMaterials";

type Line = { materialId: string; quantity: string };

/**
 * Materialen per keer bij een onderhoudsbeurt (module Materialen, issue #173). Ze gaan mee naar de taak
 * *Onderhoud plannen* en tellen in de MJOP op bij *per keer*. Elke wijziging slaat meteen op.
 */
export function ObligationMaterials({
  obligationId,
  buildingId,
  canEdit,
  showCost,
}: {
  obligationId: string;
  buildingId: string;
  canEdit: boolean;
  showCost: boolean;
}) {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useObligationMaterials(obligationId);
  const edit = canEdit && hasModule(me, "materials", "edit");
  const catalog = useMaterials(buildingId, { enabled: edit });
  const [lines, setLines] = useState<Line[] | null>(null);
  if (!hasModule(me, "materials")) return null;
  const data = q.data;
  const shown: Line[] =
    lines ?? data?.lines.map((l) => ({ materialId: l.materialId, quantity: formatQty(l.quantity) })) ?? [];

  async function save(next: Line[]) {
    setLines(next);
    const valid = next.filter((l) => l.materialId && parseQty(l.quantity));
    try {
      const r = await api<ObligationMaterialsResponse>(`/asset-obligations/${obligationId}/materials`, {
        method: "PUT",
        body: { lines: valid.map((l) => ({ materialId: l.materialId, quantity: parseQty(l.quantity) })) },
      });
      qc.setQueryData(["obligation-materials", obligationId], r);
      void qc.invalidateQueries({ queryKey: ["mjop"] });
      void qc.invalidateQueries({ queryKey: materialsKey(buildingId) });
    } catch (e) {
      toast(errMsg(e));
    }
  }

  const options = (catalog.data ?? []).filter((m) => !m.archived);
  const unitOf = (id: string) =>
    options.find((m) => m.id === id)?.unit ?? data?.lines.find((l) => l.materialId === id)?.unit ?? "";
  const totalOf = (id: string) => data?.lines.find((l) => l.materialId === id)?.totalCents ?? null;

  return (
    <section className="pl-sect mat-ob" aria-label="Materialen per keer">
      <h3>Materialen per keer</h3>
      {!data ? (
        <Loading small />
      ) : edit ? (
        <>
          {shown.map((l, i) => (
            <div className="mat-line" key={`${i}-${l.materialId}`}>
              <select
                aria-label="Artikel"
                value={l.materialId}
                onChange={(e) => void save(shown.map((x, j) => (j === i ? { ...x, materialId: e.target.value } : x)))}
              >
                <option value="">Kies een artikel…</option>
                {options
                  .filter((m) => m.id === l.materialId || !shown.some((x) => x.materialId === m.id))
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                      {m.articleNo ? ` (${m.articleNo})` : ""}
                    </option>
                  ))}
              </select>
              <input
                aria-label="Aantal per keer"
                className="num"
                inputMode="decimal"
                defaultValue={l.quantity}
                onBlur={(e) =>
                  e.target.value !== l.quantity &&
                  void save(shown.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))
                }
              />
              <span className="muted small">{unitOf(l.materialId)}</span>
              {showCost && (
                <span className="num small">
                  {totalOf(l.materialId) != null ? formatEuro(totalOf(l.materialId)!) : ""}
                </span>
              )}
              <button
                type="button"
                className="btn ghost small ico"
                aria-label="Regel verwijderen"
                onClick={() => void save(shown.filter((_, j) => j !== i))}
              >
                <IconTrash size={18} aria-hidden="true" />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn small ghost"
            onClick={() => setLines([...shown, { materialId: "", quantity: "1" }])}
            disabled={!options.length}
          >
            <IconPlus size={18} aria-hidden="true" /> Materiaal
          </button>
          {!options.length && catalog.data && (
            <p className="muted small">Nog geen artikelen voor dit gebouw. Voeg ze toe onder Materialen.</p>
          )}
        </>
      ) : data.lines.length ? (
        <ul className="dir-items">
          {data.lines.map((l) => (
            <li key={l.materialId}>
              <span className="grow">{l.name}</span>
              <span className="num">
                {formatQty(l.quantity)} {l.unit}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted small">Geen materialen.</p>
      )}
      {showCost && data && data.totalCents > 0 && (
        <p className="hint">
          Materialen {formatEuro(data.totalCents)} per keer, telt in de MJOP op bij het bedrag per keer.
        </p>
      )}
    </section>
  );
}
